<template>
  <v-dialog
    v-model="isDialogOpen"
    @keydown.escape="closeDialog"
    @after-leave="resetDialogProps"
    transition="slide-left"
    content-class="drawer-like"
    v-bind="$attrs">
    <v-card class="dialog-box" data-testid="edit-task-dialog">
      <v-card-title class="d-flex flex-row align-center">
        <h2 class="typography-heading-lg">{{ t('global.basicTerms.tasks') }} #{{ taskNumber }}</h2>
        <v-spacer></v-spacer>
        <v-menu v-if="showMenuOptions">
          <template v-slot:activator="{ props }">
            <v-btn icon="mdi-dots-vertical" variant="text" v-bind="props"></v-btn>
          </template>
          <v-list density="compact">
            <v-list-item
              disabled
              prepend-icon="mdi-content-copy"
              :title="t('yard.drawers.editTaskDrawer.labels.cloneTask')"></v-list-item>

            <v-list-item
              disabled
              prepend-icon="mdi-check"
              :title="t('yard.drawers.editTaskDrawer.labels.complete')"></v-list-item>

            <v-list-item
              disabled
              prepend-icon="mdi-close"
              :title="t('yard.drawers.editTaskDrawer.labels.cancel')"></v-list-item>
          </v-list>
        </v-menu>
        <v-btn variant="text" icon="mdi-close" @click="closeDialog" />
      </v-card-title>

      <template v-if="isLoading">
        <div class="d-flex justify-center align-center h-100 px-3">
          <v-progress-linear indeterminate color="primary"></v-progress-linear>
        </div>
      </template>
      <template v-else>
        <v-card-text v-if="!isLoading && currentTask" class="d-flex flex-column gap-4 p-0">
          <div class="yard-task-header">
            <div
              class="yard-task-header__status"
              :class="`yard-task-header__status--${taskStatus}`">
              <v-icon
                :icon="taskStatusIcon"
                size="16"
                class="yard-task-header__status__icon"></v-icon>
              <span class="typography-caption-bold yard-task-header__status__label">
                {{ taskStatusLabel }}
              </span>
            </div>

            <div class="yard-task-header__status-by">
              <span class="typography-caption-bold yard-task-header__status-by__label">
                {{ taskStatusBy }}
              </span>
              <span class="typography-caption yard-task-header__status-by__value">
                {{ taskStatusByValue }}
              </span>
            </div>

            <div class="yard-task-header__status-at">
              <span class="typography-caption-bold yard-task-header__status-at__label">
                {{ taskStatusAt }}
              </span>
              <span class="typography-caption yard-task-header__status-at__value">
                {{ taskStatusAtValue }}
              </span>
              <elapsed-time
                v-if="inProgressEvent && completedEvent"
                class="yard-task-header__status-at__elapsed-time"
                variant="human"
                :from="inProgressEvent.rawAt"
                :until="completedEvent.rawAt"
                :timezone="warehouseTimezone"
                :update-interval="0" />
            </div>
          </div>

          <div class="flex-grow-1 d-flex flex-column gap-4">
            <v-tabs v-model="tab" color="primary" class="custom-tabs mx-6 my-0">
              <v-tab value="task">{{ t('yard.drawers.editTaskDrawer.tabs.task') }}</v-tab>
              <v-tab value="activity">
                {{ t('yard.drawers.editTaskDrawer.tabs.commentsAndActivity') }}
              </v-tab>
            </v-tabs>

            <v-tabs-window v-model="tab" class="custom-tabs-window">
              <v-tabs-window-item value="task">
                <div class="pt-0 px-6 pb-4">
                  <component
                    :is="getTaskFormComponent()"
                    v-model="currentTask"
                    :disabledFields="disabledFieldsConfig"
                    @validate="handleValidate" />
                </div>
              </v-tabs-window-item>
              <v-tabs-window-item value="activity" class="flex-grow-1">
                <div class="d-flex flex-column h-100">
                  <div class="pt-0 px-6 pb-4">
                    <yard-task-timeline
                      :task-id="taskId"
                      :warehouse-timezone="warehouseTimezone"
                      :is-military-time-enabled="isMilitaryTimeEnabled" />
                  </div>
                </div>
              </v-tabs-window-item>
            </v-tabs-window>
          </div>
        </v-card-text>
        <template v-if="tab === 'task'">
          <v-divider></v-divider>
          <v-card-actions class="pa-4">
            <v-btn
              variant="text"
              @click="closeDialog"
              data-testid="edit-task-dialog-action-nevermind">
              {{ t('global.buttons.nevermind') }}
            </v-btn>
            <primary-button
              :disabled="isSubmitDisabled"
              @click="updateTask"
              data-testid="edit-task-dialog-action-save">
              {{ t('global.buttons.save') }}
            </primary-button>
          </v-card-actions>
        </template>
      </template>
    </v-card>
  </v-dialog>
</template>

<script>
import { defineComponent, ref, computed, onMounted, onUnmounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { isEmpty } from 'lodash';

import { useDialogListener, useNotify } from '@/composables';
import { YardTaskStatus } from '@nova/core';
import yardTasksService from '@satellite/services/yard-tasks-service';
import useYard from '@/modules/yard/composables/useYard';

import ElapsedTime from '@satellite/components/elements/ElapsedTime.vue';
import YardTasksCreateMoveTaskForm from './YardTasksCreateMoveTaskForm.vue';
import YardTasksCreateCheckTaskForm from './YardTasksCreateCheckTaskForm.vue';
import YardTaskTimeline from '@satellite/components/YardTaskTimeline/YardTaskTimeline.vue';
import { transformEditDataToApiRequestData } from './EventTriggeredEditTaskDrawer.helpers';
import { useYardTaskPresenter } from './useYardTaskPresenter';
import { createTaskDisabledFields, hasTaskChanged } from './EventTriggeredEditTaskDrawer.helpers';

export default defineComponent({
  name: 'EventTriggeredEditTaskDrawer',
  components: {
    ElapsedTime,
    YardTasksCreateMoveTaskForm,
    YardTasksCreateCheckTaskForm,
    YardTaskTimeline
  },
  setup() {
    const { t } = useI18n();
    const notify = useNotify();
    const yardStore = useYard();
    const {
      isDialogOpen,
      dialogProps,
      closeDialog,
      resetDialogProps,
      subscribeToDialogEvent,
      unsubscribeFromDialogEvent
    } = useDialogListener('open-edit-yard-task-drawer');

    onMounted(() => {
      subscribeToDialogEvent();
    });

    onUnmounted(() => {
      unsubscribeFromDialogEvent();
    });

    const tab = ref('task');
    const isSubmitDisabled = ref(true);

    const taskId = computed(() => dialogProps.value?.taskId);
    const {
      isLoading,
      currentTask: presenterTask,
      transformedTask,
      taskNumber,
      statusIcon: taskStatusIcon,
      statusLabel: taskStatusLabel,
      isTaskInProgress,
      isTaskCompleted,
      isTaskCancelled,
      inProgressEvent,
      completedEvent,
      createdAt: taskCreatedAt,
      createdBy: taskCreatedBy,
      inProgressBy: taskInProgressBy,
      completedBy: taskCompletedBy,
      cancelledBy: taskCancelledBy,
      inProgressAt: taskInProgressAt,
      completedAt: taskCompletedAt,
      cancelledAt: taskCancelledAt,
      warehouseTimezone
    } = useYardTaskPresenter({ taskId });

    const currentTask = ref(null);

    const taskType = computed(() => {
      return presenterTask.value?.type;
    });

    const taskStatus = computed(() => {
      return presenterTask.value?.status;
    });

    const disabledFieldsConfig = computed(() => {
      if (!taskStatus.value || !taskType.value) {
        return {};
      }

      const context = {
        taskStatus: taskStatus.value,
        taskType: taskType.value
      };

      return createTaskDisabledFields(context);
    });

    const taskStatusBy = computed(() => {
      if (isTaskInProgress.value) {
        return t('yard.drawers.editTaskDrawer.labels.inProgressBy');
      } else if (isTaskCompleted.value) {
        return t('yard.drawers.editTaskDrawer.labels.completedBy');
      } else if (isTaskCancelled.value) {
        return t('yard.drawers.editTaskDrawer.labels.cancelledBy');
      }

      return t('yard.drawers.editTaskDrawer.labels.createdBy');
    });

    const taskStatusByValue = computed(() => {
      if (isTaskInProgress.value) {
        return taskInProgressBy.value;
      } else if (isTaskCompleted.value) {
        return taskCompletedBy.value;
      } else if (isTaskCancelled.value) {
        return taskCancelledBy.value;
      }

      return taskCreatedBy.value;
    });

    const taskStatusAt = computed(() => {
      if (isTaskInProgress.value) {
        return t('yard.drawers.editTaskDrawer.labels.startedAt');
      } else if (isTaskCompleted.value) {
        return t('yard.drawers.editTaskDrawer.labels.completedAt');
      } else if (isTaskCancelled.value) {
        return t('yard.drawers.editTaskDrawer.labels.cancelledAt');
      }

      return t('yard.drawers.editTaskDrawer.labels.createdAt');
    });

    const taskStatusAtValue = computed(() => {
      if (isTaskInProgress.value) {
        return taskInProgressAt.value;
      } else if (isTaskCompleted.value) {
        return taskCompletedAt.value;
      } else if (isTaskCancelled.value) {
        return taskCancelledAt.value;
      }

      return taskCreatedAt.value;
    });

    const isMilitaryTimeEnabled = computed(() => {
      return yardStore.isMilitaryTimeEnabled.value;
    });

    const showMenuOptions = computed(() => {
      return (
        !isLoading.value &&
        currentTask.value &&
        [YardTaskStatus.CREATED, YardTaskStatus.IN_PROGRESS].includes(taskStatus.value)
      );
    });

    watch(transformedTask, newTransformedTask => {
      if (newTransformedTask) {
        currentTask.value = newTransformedTask;
        isSubmitDisabled.value = true;
      } else {
        currentTask.value = null;
        isSubmitDisabled.value = true;
      }
    });

    const getTaskFormComponent = () => {
      if (taskType.value === 'move') {
        return YardTasksCreateMoveTaskForm;
      }

      if (taskType.value === 'check') {
        return YardTasksCreateCheckTaskForm;
      }

      return null;
    };

    const handleValidate = isValid => {
      isSubmitDisabled.value =
        isEmpty(transformedTask.value) ||
        !hasTaskChanged(currentTask.value, transformedTask.value) ||
        !isValid;
    };

    const updateTask = async () => {
      if (!dialogProps.value) {
        console.error('Failed to create check task: No dialog props found');
        return;
      }

      const payload = transformEditDataToApiRequestData(
        currentTask.value,
        dialogProps.value?.warehouseId,
        dialogProps.value?.warehouseTimezone
      );

      if (!payload) {
        console.error('Failed to build task payload');
        return;
      }

      let task = null;
      try {
        task = await yardTasksService.updateYardTask(presenterTask.value.id, payload);

        notify(t(`yard.messages.${taskType.value}TaskUpdated`), 'success');
      } catch (error) {
        notify(t('yard.messages.failedToUpdateYardTask'), 'error');
        return;
      }

      try {
        dialogProps.value.onTaskUpdated?.(task);
      } catch (error) {
        console.error('Error in post-update callback or dialog close:', error);
      }

      closeDialog();
    };

    watch(isDialogOpen, newIsOpen => {
      if (!newIsOpen) {
        tab.value = 'task';
      }
    });

    return {
      tab,
      isLoading,
      taskId,
      isMilitaryTimeEnabled,
      currentTask,
      isDialogOpen,
      isSubmitDisabled,
      taskNumber,
      taskStatus,
      taskStatusIcon,
      taskStatusLabel,
      isTaskCompleted,
      inProgressEvent,
      completedEvent,
      taskStatusBy,
      taskStatusAt,
      taskStatusByValue,
      taskStatusAtValue,
      disabledFieldsConfig,
      warehouseTimezone,
      showMenuOptions,
      t,
      closeDialog,
      resetDialogProps,
      getTaskFormComponent,
      handleValidate,
      updateTask
    };
  }
});
</script>

<style scoped lang="scss">
// TODO: (welington) extract the drawer-like styles to a shared component.
:deep(.drawer-like) {
  width: 620px;
  max-width: 100%;
  position: fixed;
  right: 0;
  margin: 0;
  height: 100vh;
  max-height: 100%;

  .v-card {
    border-start-end-radius: 0;
    border-end-end-radius: 0;
  }
}

.yard-task-header {
  display: flex;
  align-items: stretch;
  justify-content: space-between;
  border-radius: 4px;
  border: 1px solid $color-line-border;
  background-color: $color-neutral-10;

  margin: $spacing-4 $spacing-6 0;
}

.yard-task-header__status {
  flex: 1;

  --yard-task-header-status-background-color: #{$color-neutral-10};
  --yard-task-header-status-icon-color: #{$color-neutral-80};
  --yard-task-header-status-label-color: #{$color-neutral-90};

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-0-5;
  padding: $spacing-2 $spacing-3;

  border-radius: 4px 0 0 4px;

  background-color: var(--yard-task-header-status-background-color);
}

.yard-task-header__status__icon {
  color: var(--yard-task-header-status-icon-color);
}

.yard-task-header__status__label {
  color: var(--yard-task-header-status-label-color);
}

.yard-task-header__status {
  &--in-progress {
    --yard-task-header-status-background-color: #{$color-primary-20};
    --yard-task-header-status-icon-color: #{$color-primary-100};
  }

  &--completed {
    --yard-task-header-status-background-color: #{$color-success-20};
    --yard-task-header-status-icon-color: #{$color-success-60};
  }

  &--cancelled {
    --yard-task-header-status-background-color: #{$color-error-20};
    --yard-task-header-status-icon-color: #{$color-error-60};
  }
}

.yard-task-header__status-by {
  flex: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-0-5;
  padding: $spacing-2 $spacing-3;

  border-left: 1px solid $color-line-border;
}

.yard-task-header__status-at {
  flex: 1;

  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: $spacing-0-5;
  padding: $spacing-2 $spacing-3;

  border-left: 1px solid $color-line-border;
}

.yard-task-header__status-by__label,
.yard-task-header__status-at__label {
  color: $color-text-secondary;
}

.yard-task-header__status-by__value,
.yard-task-header__status-at__value {
  color: $color-text-primary;
}

.yard-task-header__status-at__elapsed-time {
  text-align: center;
  font-family: Barlow;
  font-size: 10px;
  font-style: normal;
  font-weight: 600;
  line-height: 140%; /* 14px */
}
</style>
